import { ui } from "@/components/ui";
import type { RouteT } from "@/route-types";
import { Link, useMatches } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import type { StatefulDiaryT } from "../../../types";
import { useFormsToDo } from "../features/new/hooks/useFormsToDo";

type TabOptions = "new" | "history" | "charts";
const DiaryViewTabs = ({ diary }: { diary: StatefulDiaryT }) => {
	const { toDo } = useFormsToDo(
		diary?.trackers_view || [],
		diary?.observations_view || [],
	);

	const [tab, setTab] = useState<TabOptions>("new");
	const matches = useMatches();
	const currentPath = matches[matches.length - 1]?.pathname ?? "";

	const getCurrentTab = useCallback(() => {
		if (currentPath.includes("charts")) {
			return "charts";
		}
		if (currentPath.includes("history")) {
			return "history";
		}
		return "new";
	}, [currentPath]);

	useEffect(() => {
		setTab(getCurrentTab());
	}, [getCurrentTab]);

	return (
		<ui.Tabs value={tab}>
			<ui.TabsList className="p-[5px] rounded-md w-full h-16">
				<Tab value="new" to="/diaries/$diaryId/home/new">
					<div className="flex justify-center items-center gap-2">
						Forms
						{toDo.length > 0 && (
							<span className="flex justify-center items-center bg-indigo-200 dark:bg-indigo-800 px-1.5 rounded-full min-w-[20px] h-6 aspect-square font-medium text-indigo-800 dark:text-indigo-200 text-xs">
								{toDo.length}
							</span>
						)}
					</div>
				</Tab>
				<Tab value="history" to="/diaries/$diaryId/home/history">
					History
				</Tab>
				<Tab value="charts" to="/diaries/$diaryId/home/charts">
					Charts
				</Tab>
			</ui.TabsList>
		</ui.Tabs>
	);
};

export default DiaryViewTabs;

interface TabProps {
	value: string;
	to: RouteT;
	children: React.ReactNode;
}

const Tab = (props: TabProps) => {
	return (
		<ui.TabsTrigger
			asChild
			className="rounded-md w-full h-full text-lg"
			value={props.value}
		>
			<Link className="capitalize" to={props.to} params={true}>
				{props.children}
			</Link>
		</ui.TabsTrigger>
	);
};
